import { PureComponent } from "react";
import { connect } from "react-redux";
import { User } from "common/react-plucked";
import { RouteComponentProps, withRouter } from "react-router-dom";
import once from "lodash/once";

import * as uiStateActions from "../../../../reducers/uistate";
import { UserDataAndState, isUserData } from "common/Database";
import { RootState } from "../../../../store/types";
import { IconPanelButton, ImagePanelButton } from "../../../builder/ui/smart/left-panel/PanelButton";
import { SignInUpModal } from "../../../modals/smart/sign-in-up-modal/sign-in-up-modal";
import { isSample } from "common/database-strings";
import { onAuthStateChanged } from "../../../../lib/firebase-auth";
import { dontAwait, parseAnonymousUserID } from "common/Support";
import { withAppID } from "designer/lib/with-app-id";
import { withModalProvider } from "designer/modals-v2/modal-provider-v2/modal-provider-v2";
import { SimpleModalContextProps } from "designer/modals-v2/modal-provider-v2/modal-context";
import WindowedModalV2 from "designer/modals-v2/windowed-modal/windowed-modal";

interface Props extends RouteComponentProps {
    userData: UserDataAndState;
    appID: string | undefined;
    updateLeftPanelState: typeof uiStateActions.updateLeftPanelState;
    proPlans: number;
    unlockApp?: () => Promise<void>;
    modals: SimpleModalContextProps;
}

interface State {
    signingIn: boolean;
    copySampleAppID: string | undefined;
}

class UserControl extends PureComponent<Props, State> {
    public state: State = { signingIn: false, copySampleAppID: undefined };

    private onClick(currentAppID: string): void {
        const copySample = isSample(currentAppID);
        if (copySample) {
            this.props.updateLeftPanelState({ copyingSample: true });
        }

        this.showSignInUpModal(copySample ? "signUp" : "signIn", copySample ? currentAppID : undefined);
    }

    private showSignInUpModal(startingMode: "signUp" | "signIn", copySampleId: string | undefined) {
        this.setState({ signingIn: true });

        const onFinish = () => {
            this.setState({ signingIn: false, copySampleAppID: copySampleId });
        };

        dontAwait(
            this.props.modals.showModal<void>(resolve => {
                return (
                    <WindowedModalV2 canClose={true} hideClosingButton={true} onClose={resolve}>
                        <SignInUpModal startingMode={startingMode} onFinish={onFinish} />
                    </WindowedModalV2>
                );
            })
        );
    }

    public componentDidMount() {
        const params = new URLSearchParams(window.location.search);
        const signingIn = params.get("signIn") !== null;
        const signingUp = params.get("signUp") !== null;

        if (signingIn || signingUp) {
            onAuthStateChanged(
                once(auth => {
                    if (auth !== undefined && parseAnonymousUserID(auth.uid) === undefined) return;

                    this.showSignInUpModal(signingIn ? "signIn" : "signUp", undefined);
                })
            );
        }
    }

    public render(): JSX.Element {
        const { userData, appID } = this.props;

        if (!isUserData(userData)) {
            const isSampleApp = isSample(appID ?? "");

            if (isSampleApp) {
                // FIXME: This becomes a big, fat CTA
                return (
                    <IconPanelButton
                        isEnabled={!this.state.signingIn}
                        isProfileIcon={true}
                        isSample={true}
                        extendBackground={true}
                        icon={User}
                        tooltipContent={this.state.signingIn ? "Signing up…" : "Sign Up to Edit & Share"}
                        onClick={async () => this.onClick(appID ?? "")}
                    />
                );
            } else {
                return (
                    <IconPanelButton
                        isEnabled={!this.state.signingIn}
                        isProfileIcon={true}
                        isSample={false}
                        icon={User}
                        tooltipContent={this.state.signingIn ? "Signing in…" : "Sign In"}
                        onClick={async () => this.onClick(appID ?? "")}
                    />
                );
            }
        }

        return <ImagePanelButton imageURL={userData.photoURL} tooltipContent={userData.displayName} />;
    }
}

const mapState = (state: RootState) => ({
    userData: state.userData,
    proPlans: state.userData !== null && state.userData !== "loading" ? state.userData.planCounts.pro : 0,
});

const mapDispatch = {
    updateLeftPanelState: uiStateActions.updateLeftPanelState,
};

export default withModalProvider(withAppID(withRouter(connect(mapState, mapDispatch)(UserControl))));
